import { Fragment, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import { LanguageTexts } from '@/utils/languages.utils';

interface Props {
  availableLanguages: TAvailableLanguageCodes[];
  renderText: (language: TAvailableLanguageCodes) => ReactNode;
}

export const LanguageExpandableText = ({ availableLanguages, renderText }: Props) => {
  const { t, i18n } = useTranslation('Templates');
  const [expanded, setExpanded] = useState(false);

  if (!availableLanguages.length) return null;

  const defaultLanguage =
    availableLanguages.find((language) => language.startsWith(i18n.language)) ?? availableLanguages[0];
  const otherLanguages = availableLanguages.filter((language) => language !== defaultLanguage);

  const label = (language: TAvailableLanguageCodes) => LanguageTexts[language as never] ?? language;

  const row = (language: TAvailableLanguageCodes) => (
    <Fragment key={language}>
      <span className="text-xs whitespace-nowrap text-gray-500">{label(language)}</span>
      <div>{renderText(language)}</div>
    </Fragment>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[auto_1fr] items-start gap-x-3">{row(defaultLanguage)}</div>

      {otherLanguages.length ? (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="text-primary-500 hover:text-primary-600 flex w-fit items-center gap-1 text-sm"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {t('question.rules.showMoreLanguages', { count: otherLanguages.length })}
          </button>

          {expanded ? (
            <div className="grid grid-cols-[auto_1fr] items-start gap-x-3 gap-y-2">{otherLanguages.map(row)}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
